import cn from 'classnames';
import type { FC } from 'react';
import {
  type ComponentProps,
  type SetStateAction,
  type UIEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useIntl, type IntlShape } from 'react-intl';
import { useLocation, useNavigate } from 'react-router';
import {
  type Components as StreamdownComponents,
  type PluginConfig as StreamdownPluginConfig,
} from 'streamdown';
import '@fontsource/inter/400.css';
import '@fontsource/inter/600.css';
import 'streamdown/styles.css';

import {
  type AgentCoreApproval,
  type AgentCoreContext,
  AgentCoreConversationMessage,
  AgentCoreResolveResponse,
  AgentCoreRunEvent,
  AgentCoreThreadSummary,
  type AgentThreadSubject,
  createAgentCoreThread,
  getAgentCoreThread,
  uploadChatAttachment,
  listAgentCoreThreads,
  resolveAgentCoreContext,
  streamAgentCoreRun,
} from '@/shared/api/agentCore/agentCoreApi';
import api from '@/shared/api/api';
import {
  DeletePatientDocumentReq,
  ListPatientDocumentSourceAssetsReq,
} from '@/shared/api/protocol-ts/api/core/svc_patient_document_pb';
import { useAppDispatch, useAppSelector, useMedia } from '@/shared/hooks';
import { buildTemplatesUrl, getImageSrc } from '@/shared/lib';
import {
  type AgentChatFloatingSize,
  readChatFloatingMaximized,
  readChatFloatingSize,
  readChatOpen,
  readChatSidebarWidth,
  readChatSubjectDrafts,
  writeChatFloatingMaximized,
  writeChatFloatingSize,
  writeChatOpen,
  writeChatSidebarWidth,
  writeChatSubjectDraft,
} from '@/shared/lib/agentChatPreferences';
import { stripChatQuery } from '@/shared/lib/stripChatQuery';
import { Icon, Spinner } from '@/shared/ui';

import { encounterModel } from '@/entities/encounter';
import { organizationModel } from '@/entities/organization';
import { patientModel } from '@/entities/patient';
import { reportsModel, useNavigateToReport } from '@/entities/reports';
import { templatesModel } from '@/entities/templates';
import { userModel } from '@/entities/user';

import {
  type ActivityItem,
  type AgentChatProps,
  type ComposerAttachment,
  DRAFT_THREAD_KEY,
  EMPTY_THREAD_ITEMS,
  patientAgentMessages,
  type ThreadApprovalItem,
  type ThreadItem,
  type ThreadItemsByThreadID,
} from '../config';
import { useAgentChatThread, useChatDocking } from '../hooks';
import { createMessageID } from '../lib';
import {
  buildActor,
  buildStarterPrompts,
  getCanonicalContext,
  getReportContextLabel,
  getErrorMessage,
  getStringValue,
  isOrganizationMismatchError,
  presentActivity,
  upsertThreadDocument,
  waitFor,
  extractOnboardingFlow,
  type OnboardingFlow,
  serializePageDom,
} from '../lib';

import styles from './AgentChat.module.scss';
import { ApprovalCard } from './ApprovalCard';
import { ChatBody } from './ChatBody';
import {
  getCollapsedThreadPreview,
  getLatestRunningEvent,
} from './collapsedThreadPreview';
import { type CollapsedThreadPreviewItem } from './collapsedThreadPreview';
import { Composer } from './Composer';
import { filesFromClipboardData, trimInsertedPrompt } from './composerInput';
import {
  shouldInsertComposerNewline,
  shouldSubmitComposer,
} from './composerKeyboard';
import { makeOnboardingRenderer } from './OnboardingFlowCard';
import { OnboardingTour } from './OnboardingTour';
import {
  countUserPrompts,
  hasCompletedTurnAfterPrompt,
  hasExceededRecoveryWindow,
  hasUserPrompt,
  shouldStopUnacceptedRecovery,
} from './streamRecovery';
import { isThreadArchived } from './threadList';
import { createThreadLoadCoordinator } from './threadLoadCoordinator';

// Mirrors core.s ChatUploadMaxSize (50 MiB) and agent-core.s per-message
// attachment cap. Enforced client-side for a friendly early error; the server
// re-enforces both.
export const CHAT_UPLOAD_MAX_SIZE = 50 * 1024 * 1024;
// Builds a subject object from its primitive fields. Hosts rebuild subject
// objects on every render, so components hold the primitives in dependency
// arrays and build the object only where an API call or prop needs it.
const buildThreadSubject = (
  type?: AgentThreadSubject['type'],
  id?: string,
): AgentThreadSubject | null => (type && id ? { type, id } : null);
// Margin the maximize toggle keeps from the viewport edges.
const FLOATING_VIEWPORT_MARGIN = 40;

// Where the expand toggle stashes the dragged size while expanded.
// Not persisted: refreshing while expanded keeps the expanded size.
// Sidebar width drag bounds.
const SIDEBAR_MIN_WIDTH = 160;
const SIDEBAR_MAX_WIDTH = 360;
const SIDEBAR_DEFAULT_WIDTH = 220;
// Drag bounds for the floating popup, matching startFloatingResize.
const FLOATING_MIN_SIZE = 360;

// Fully expanded pane: fills the viewport minus a margin at the top and
// the 22px bottom anchor.
const getMaximizedFloatingSize = () => ({
  width: window.innerWidth - FLOATING_VIEWPORT_MARGIN,
  height: window.innerHeight - FLOATING_VIEWPORT_MARGIN - 22,
});

// Stored popup sizes are px values captured at drag time; keep them inside
// the current viewport so a browser resize never leaves the pane overflowing
// the page (the inline px sizes override the stylesheet's vw/vh clamps).
const clampFloatingSize = (size: { width?: number; height?: number }) => ({
  width:
    size.width === undefined
      ? undefined
      : Math.min(
          Math.max(FLOATING_MIN_SIZE, size.width),
          window.innerWidth - 48,
        ),
  height:
    size.height === undefined
      ? undefined
      : Math.min(
          Math.max(FLOATING_MIN_SIZE, size.height),
          window.innerHeight - 22,
        ),
});
// Popup width at or above which the floating sidebar renders as a static
// column instead of a shutter.
const FLOATING_SIDEBAR_WIDE_PX = 1000;
const DRAFT_SAVE_DEBOUNCE_MS = 400;
export const CHAT_UPLOAD_MAX_PER_MESSAGE = 5;

const FloatingAgentMark = () => <Icon name="cat" size={36} />;

const toCollapsedPreviewItems = (
  items: ThreadItem[],
): CollapsedThreadPreviewItem[] =>
  items.map((item) => {
    switch (item.type) {
      case 'message':
        return {
          type: 'message',
          role: item.role,
          content: item.content,
        };
      case 'event':
        return {
          type: 'event',
          label: item.label,
          detail: item.detail,
          tone: item.tone,
        };
      case 'document':
        return { type: 'document', title: item.title };
      case 'approval':
        return { type: 'document', title: item.title };
    }
  });

const upsertThreadEvent = (items: ThreadItem[], nextItem: ActivityItem) => {
  const existingIndex = items.findIndex((item) => item.id === nextItem.id);
  const existingItem = existingIndex >= 0 ? items[existingIndex] : undefined;
  const threadEvent: ThreadItem = {
    id: nextItem.id,
    type: 'event',
    tone: nextItem.tone,
    label: nextItem.label,
    completedAt: nextItem.completedAt,
    detail: nextItem.detail,
    startedAt:
      existingItem?.type === 'event'
        ? existingItem.startedAt
        : nextItem.startedAt,
    visual:
      nextItem.visual ??
      (nextItem.tone === 'running'
        ? 'progress'
        : nextItem.tone === 'error'
          ? 'error'
          : 'check'),
  };

  if (existingIndex < 0) {
    return [...items, threadEvent];
  }

  return items.map((item, index) =>
    index === existingIndex ? threadEvent : item,
  );
};

const updateThreadMessageAtEnd = (
  items: ThreadItem[],
  id: string,
  updater: (
    message: Extract<ThreadItem, { type: 'message' }>,
  ) => Extract<ThreadItem, { type: 'message' }>,
) => {
  const nextItems: ThreadItem[] = [];
  let updatedMessage: Extract<ThreadItem, { type: 'message' }> | null = null;

  items.forEach((item) => {
    if (item.type === 'message' && item.id === id) {
      updatedMessage = updater(item);
      return;
    }

    nextItems.push(item);
  });

  if (updatedMessage) {
    nextItems.push(updatedMessage);
  }

  return nextItems;
};

const StreamdownHeading = ({
  children,
  ...props
}: ComponentProps<'h1'> & { node?: unknown }) => {
  const paragraphProps = { ...props } as ComponentProps<'p'> & {
    node?: unknown;
  };
  delete paragraphProps.node;

  return (
    <p {...paragraphProps}>
      <strong>{children}</strong>
    </p>
  );
};

const streamdownComponents: StreamdownComponents = {
  h1: StreamdownHeading,
  h2: StreamdownHeading,
  h3: StreamdownHeading,
  h4: StreamdownHeading,
  h5: StreamdownHeading,
  h6: StreamdownHeading,
  /* Tables render like the patient-documents preview: a bordered, rounded,
     horizontally scrollable block with a min-width table — the default
     Streamdown wrapper squeezes wide tables unreadable in the pane. */
  table: ({ children }) => (
    <div className={styles.messageTableScroll}>
      <table>{children}</table>
    </div>
  ),
};

type EvidenceReference = {
  id: string;
  kind: 'asset' | 'report' | 'study' | 'document';
  reportID: string;
};

type SourceAssetReference = {
  assetID: string;
  reportID: string;
};

const createAssetReferenceID = async (assetID: string) => {
  const digest = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(assetID),
  );
  const hex = Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, '0'),
  ).join('');

  return `image_${hex.slice(0, 12)}`;
};

const parseEvidenceReference = (href?: string): EvidenceReference | null => {
  if (!href) {
    return null;
  }
  const match = href.match(
    /^\/agent-ref\/(asset|report|study|document)\/([^/?#]+)(?:\?([^#]*))?$/,
  );
  if (!match) {
    return null;
  }
  try {
    return {
      kind: match[1] as EvidenceReference['kind'],
      id: decodeURIComponent(match[2]),
      reportID: new URLSearchParams(match[3] || '').get('report') || '',
    };
  } catch {
    return null;
  }
};

const toThreadItems = (
  threadID: string,
  messages: AgentCoreConversationMessage[],
  formatMessage: IntlShape['formatMessage'],
): ThreadItem[] => {
  let items: ThreadItem[] = [];
  let responseStartedAt: number | undefined;
  messages.forEach((message, index) => {
    if (message.role === 'approval') {
      items = [
        ...items,
        {
          id: message.approval.artifactID || `${threadID}_approval_${index}`,
          type: 'approval',
          proposalID: message.approval.proposalID,
          kind: message.approval.kind,
          title: message.approval.title,
          preview: message.approval.preview,
          status: 'pending',
        },
      ];
      return;
    }
    if (message.role === 'document') {
      items = upsertThreadDocument(items, {
        id: message.artifact.artifactID || `${threadID}_document_${index}`,
        type: 'document',
        documentID: message.artifact.documentID,
        title: message.artifact.title,
        action: message.artifact.action,
        assetCount: message.artifact.assetCount,
        revision: message.artifact.revision,
      });
      return;
    }
    if (message.role === 'catalog') {
      // Applied catalog changes are page-refresh signals, not chat content.
      return;
    }
    if (message.role === 'activity') {
      const activity = message.activity;
      const fallbackID = `${threadID}_activity_${index}`;
      const summary = presentActivity(activity, formatMessage);
      items = upsertThreadEvent(items, {
        id: activity.id || fallbackID,
        tone:
          activity.state === 'completed'
            ? 'done'
            : activity.state === 'failed'
              ? 'error'
              : 'running',
        label: summary.label,
        detail: summary.detail,
        startedAt: responseStartedAt,
        visual: summary.visual ?? 'check',
      });
      return;
    }
    const createdAt = message.createdAt
      ? Date.parse(message.createdAt)
      : Number.NaN;
    if (message.role === 'assistant' && Number.isFinite(createdAt)) {
      items = items.map((item) =>
        item.type === 'event' && item.startedAt === responseStartedAt
          ? { ...item, completedAt: createdAt }
          : item,
      );
    }
    items.push({
      id: `${threadID}_${index}`,
      type: 'message',
      role: message.role,
      content: message.content,
      authorID: message.authorID,
      attachments: message.attachments,
      messageContext: message.messageContext,
      createdAt: message.createdAt,
    });
    if (message.role === 'user' && message.approval) {
      items = items.map((item) =>
        item.type === 'approval' &&
        item.proposalID === message.approval?.proposalID
          ? {
              ...item,
              status:
                message.approval.decision === 'approve'
                  ? 'approved'
                  : message.approval.decision === 'disapprove'
                    ? 'disapproved'
                    : 'feedback',
            }
          : item,
      );
    }
    responseStartedAt =
      message.role === 'user' && Number.isFinite(createdAt)
        ? createdAt
        : undefined;
  });
  return items;
};

export const AgentChat: FC<AgentChatProps> = ({
  context,
  className,
  floating = false,
  onFloatingOpenChange,
  defaultGeneral = false,
  documentMode = false,
  documentID = '',
  documentName = '',
  attachmentPreview = null,
  onAttachmentPreviewChange,
  initialThreadID = '',
  pageContext,
  onThreadChanged,
  onScopeChange,
  sidebarContent,
  onDocumentChanged,
  onOpenDocument,
  selectedDocumentText = '',
  onReplaceDocumentSelection,
  onInsertBelowDocumentSelection,
}: AgentChatProps) => {
  const currentUser = useAppSelector(userModel.selectors.selectCurrentUser);
  const currentOrganizationID = useAppSelector(
    organizationModel.selectors.selectCurrentOrganizationID,
  );
  const workspaceKey = `private:${currentUser.ID}:${currentOrganizationID}`;
  const [isGeneralDraft, setIsGeneralDraft] = useState(defaultGeneral);
  const patientID = context.patientID;
  useEffect(() => {
    setIsGeneralDraft(!patientID);
  }, [patientID]);
  useEffect(() => {
    onScopeChange?.(isGeneralDraft);
  }, [isGeneralDraft, onScopeChange]);

  // Hosts rebuild the page context (and its subject) on every render, so
  // the subject is held as primitives and the object is built only where
  // it is passed on: an unstable object in a dependency array would
  // refetch the thread list on every render.
  const pageSubjectType = pageContext?.subject?.type;
  const pageSubjectID = pageContext?.subject?.id;
  // Focus belongs to this message. Detaching clinical context also removes
  // the document focus; it never changes the selected conversation.
  const isDocumentBound = documentMode && !!documentID;
  const activeEditorKey = documentMode
    ? documentID || `draft:${patientID}`
    : undefined;
  const threadSubjectType = isGeneralDraft
    ? undefined
    : isDocumentBound
      ? 'document'
      : pageSubjectType;
  const threadSubjectID = isGeneralDraft
    ? undefined
    : isDocumentBound
      ? documentID
      : pageSubjectID;
  const threadSubject = buildThreadSubject(threadSubjectType, threadSubjectID);
  // Page-scope subject for the composer chips: stable across the general
  // (patient-less) draft, so the attach-all chip can name what it restores.
  const scopeSubject = buildThreadSubject(
    isDocumentBound ? 'document' : pageSubjectType,
    isDocumentBound ? documentID : pageSubjectID,
  );

  const scrollIdleTimersRef = useRef(
    new Map<HTMLElement, ReturnType<typeof setTimeout>>(),
  );
  const isAtBottomRef = useRef(true);
  // Monotonic counter over sendPrompt runs. A run's background reconcile is
  // applied only if no newer run has started, so a late thread fetch can
  // never clobber the next run's optimistic items.
  const runSequenceRef = useRef(0);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  // Active interactive onboarding tour (agent-guided walkthrough). Set when a
  // completed assistant message carries a valid ```onboarding flow, or when
  // the user clicks "Show me" on a flow card.
  const [onboardingFlow, setOnboardingFlow] = useState<OnboardingFlow | null>(
    null,
  );
  // The tour auto-starts once per run: the flow may arrive through the live
  // completion event or through the post-run thread reconcile (stream
  // recovery path), never through both.
  const onboardingAutoStartDoneRef = useRef(false);

  const maybeAutoStartOnboarding = useCallback((content: string) => {
    if (onboardingAutoStartDoneRef.current) {
      return;
    }
    const flow = extractOnboardingFlow(content);
    if (!flow) {
      return;
    }
    onboardingAutoStartDoneRef.current = true;
    // A page-bound flow must not auto-start over a foreign page — the card
    // stays and offers to navigate when the user presses Show me.
    if (flow.pagePath && flow.pagePath !== window.location.pathname) {
      return;
    }
    setOnboardingFlow(flow);
  }, []);
  // Stable identity: the tour reads onClose from a ref, but a stable
  // callback also keeps the tour's auto-start effect from re-firing on
  // every AgentChat render.
  const closeOnboardingFlow = useCallback(() => setOnboardingFlow(null), []);

  const handleScrollVisibility = useCallback((event: UIEvent<HTMLElement>) => {
    const scrollContainer = event.target;

    if (!(scrollContainer instanceof HTMLElement)) {
      return;
    }

    if (scrollContainer === chatViewportRef.current) {
      const isAtBottom =
        scrollContainer.scrollHeight -
          scrollContainer.scrollTop -
          scrollContainer.clientHeight <=
        24;
      isAtBottomRef.current = isAtBottom;
      setShowScrollToBottom(!isAtBottom);
    }

    scrollContainer.classList.add(styles.isScrolling);
    clearTimeout(scrollIdleTimersRef.current.get(scrollContainer));
    scrollIdleTimersRef.current.set(
      scrollContainer,
      setTimeout(() => {
        scrollContainer.classList.remove(styles.isScrolling);
        scrollIdleTimersRef.current.delete(scrollContainer);
      }, 650),
    );
  }, []);

  useEffect(
    () => () => {
      scrollIdleTimersRef.current.forEach(clearTimeout);
      scrollIdleTimersRef.current.clear();
    },
    [],
  );
  const intl = useIntl();
  const { formatMessage } = intl;
  const { isMobile } = useMedia();
  const chatViewportRef = useRef<HTMLDivElement>(null);
  const floatingFocusedRef = useRef<HTMLElement | null>(null);
  const paneRef = useRef<HTMLElement | null>(null);
  const setFloatingFocusedRef = (el: HTMLElement | null) => {
    floatingFocusedRef.current = el;
  };
  const abortControllerRef = useRef<AbortController | null>(null);
  const threadLoadCoordinatorRef = useRef(createThreadLoadCoordinator());

  const [resolved, setResolved] = useState<AgentCoreResolveResponse | null>(
    null,
  );
  const [threads, setThreads] = useState<AgentCoreThreadSummary[]>([]);
  const [loadedThread, setLoadedThread] =
    useState<AgentCoreThreadSummary | null>(null);
  const [threadItemsByThreadID, setThreadItemsByThreadID] =
    useState<ThreadItemsByThreadID>({});
  // Selected-thread pointer: per-subject map in localStorage. Restores
  // only in the matching subject scope; selection and thread creation
  // write it, navigation and "New chat" do not.
  const { threadID, restoreTarget, selectThread } = useAgentChatThread({
    workspaceKey,
  });
  const draftKey = `${workspaceKey}:${threadID || DRAFT_THREAD_KEY}`;
  const activeThreadKey = threadID || DRAFT_THREAD_KEY;
  const activeThreadKeyRef = useRef(activeThreadKey);
  activeThreadKeyRef.current = activeThreadKey;
  const threadItems =
    threadItemsByThreadID[activeThreadKey] ?? EMPTY_THREAD_ITEMS;
  // Thread switches load in the background: while the active thread's
  // history is in flight and nothing is cached for it yet, keep showing
  // the previously displayed conversation instead of a loading skeleton.
  // The skeleton only appears when there is nothing to preserve (mount).
  const activeThreadTitle =
    threads.find((thread) => thread.id === activeThreadKey)?.title ??
    (loadedThread?.id === activeThreadKey ? loadedThread.title : '');
  const setThreadItemsFor = useCallback(
    (key: string, action: SetStateAction<ThreadItem[]>) => {
      setThreadItemsByThreadID((current) => {
        const currentItems = current[key] ?? [];
        const nextItems =
          typeof action === 'function' ? action(currentItems) : action;
        return { ...current, [key]: nextItems };
      });
    },
    [],
  );
  const setThreadItems = useCallback(
    (action: SetStateAction<ThreadItem[]>) =>
      setThreadItemsFor(activeThreadKeyRef.current, action),
    [setThreadItemsFor],
  );
  const [inputValue, setInputValue] = useState('');
  const [pendingAttachments, setPendingAttachments] = useState<
    ComposerAttachment[]
  >([]);
  // Synchronous mirror of the attachment list: admission decisions (the
  // per-message cap, the uploading guard) must read the freshest state —
  // rapid consecutive drops would otherwise read a stale render closure.
  const pendingAttachmentsRef = useRef<ComposerAttachment[]>([]);
  const updatePendingAttachments = useCallback(
    (
      update: (current: ComposerAttachment[]) => ComposerAttachment[],
    ): ComposerAttachment[] => {
      pendingAttachmentsRef.current = update(pendingAttachmentsRef.current);
      setPendingAttachments(pendingAttachmentsRef.current);
      return pendingAttachmentsRef.current;
    },
    [],
  );
  // Drafts are per subject: the composer text live-mirrors into the
  // persisted subject bank, so switching subjects (or remounting the pane
  // between the patient-page and entity-page chat instances) banks the
  // unsent text and restores the destination's draft. Writes are debounced
  // (the bank re-serializes on every keystroke otherwise); a subject
  // switch or unmount flushes the pending write first.
  const adoptingDraftRef = useRef(false);
  const draftSubjectKeyRef = useRef<string | null>(null);
  const pendingDraftRef = useRef<{ key: string; value: string } | null>(null);
  const draftSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flushPendingDraft = useCallback(() => {
    if (draftSaveTimerRef.current !== null) {
      clearTimeout(draftSaveTimerRef.current);
      draftSaveTimerRef.current = null;
    }
    const pending = pendingDraftRef.current;
    if (pending) {
      writeChatSubjectDraft(pending.key, pending.value);
      pendingDraftRef.current = null;
    }
  }, []);
  useEffect(() => {
    if (draftSubjectKeyRef.current === null) {
      draftSubjectKeyRef.current = draftKey;
      const stored = readChatSubjectDrafts()[draftKey] ?? '';
      if (stored) {
        setInputValue(stored);
      }
      return;
    }
    if (draftSubjectKeyRef.current === draftKey) {
      pendingDraftRef.current = { key: draftKey, value: inputValue };
      if (draftSaveTimerRef.current !== null) {
        clearTimeout(draftSaveTimerRef.current);
      }
      draftSaveTimerRef.current = setTimeout(
        flushPendingDraft,
        DRAFT_SAVE_DEBOUNCE_MS,
      );
      return;
    }
    flushPendingDraft();
    draftSubjectKeyRef.current = draftKey;
    if (adoptingDraftRef.current) {
      adoptingDraftRef.current = false;
      writeChatSubjectDraft(draftKey, inputValue);
      return;
    }
    setInputValue(readChatSubjectDrafts()[draftKey] ?? '');
    // Explicit thread selection changes the composer and its attachments.
    pendingAttachmentsRef.current.forEach((item) => {
      if (item.previewUrl) {
        URL.revokeObjectURL(item.previewUrl);
      }
    });
    pendingAttachmentsRef.current = [];
    setPendingAttachments([]);
  }, [flushPendingDraft, inputValue, draftKey]);
  useEffect(() => () => flushPendingDraft(), [flushPendingDraft]);
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const attachmentInputRef = useRef<HTMLInputElement>(null);
  const attachmentThreadCreationRef = useRef<Promise<string> | null>(null);
  const requestedThreadRef = useRef(initialThreadID);
  requestedThreadRef.current = initialThreadID;

  const activateThread = useCallback(
    (nextThreadID: string, adoptDraft = false) => {
      if (!nextThreadID) {
        return;
      }
      const previousKey = activeThreadKeyRef.current;
      if (
        adoptDraft &&
        previousKey === DRAFT_THREAD_KEY &&
        previousKey !== nextThreadID
      ) {
        adoptingDraftRef.current = true;
        activeThreadKeyRef.current = nextThreadID;
        setThreadItemsByThreadID((current) => {
          const next = {
            ...current,
            [nextThreadID]: current[previousKey] ?? current[nextThreadID] ?? [],
          };
          if (previousKey === DRAFT_THREAD_KEY) {
            delete next[previousKey];
          }
          return next;
        });
      }
      selectThread(nextThreadID);
      onThreadChanged?.(nextThreadID);
    },
    [onThreadChanged, selectThread],
  );

  const uploadFiles = async (files: File[]) => {
    if (!files.length) {
      return;
    }
    // Uploads need a real thread to attach to. A fresh conversation (no
    // thread yet) gets its thread created up front so the first message
    // can carry files instead of demanding a message first. General
    // (patient-less) threads support chat uploads too — the file is
    // org-scoped and the general agent reads it from the thread sandbox.
    let targetThreadID = threadID;
    if (!targetThreadID) {
      try {
        setUploadingAttachment(true);
        const requestedAtStart = requestedThreadRef.current;
        if (!attachmentThreadCreationRef.current) {
          attachmentThreadCreationRef.current = createAgentCoreThread(
            getActor(),
            {
              organization_id: currentOrganizationID,
            },
          ).then((created) => created.id);
        }
        targetThreadID = await attachmentThreadCreationRef.current;
        // Explicit selection during creation wins over the late upload response.
        if (
          requestedThreadRef.current !== requestedAtStart ||
          (activeThreadKeyRef.current !== DRAFT_THREAD_KEY &&
            activeThreadKeyRef.current !== targetThreadID)
        ) {
          setUploadingAttachment(false);
          return;
        }
        activateThread(targetThreadID, true);
      } catch (reason) {
        setErrorText(getErrorMessage(reason, intl));
        setUploadingAttachment(false);
        return;
      } finally {
        attachmentThreadCreationRef.current = null;
      }
    }
    const activeThread = threads.find((thread) => thread.id === targetThreadID);
    if (activeThread && isThreadArchived(activeThread)) {
      setErrorText(formatMessage(patientAgentMessages.attachToArchived));
      return;
    }
    const maxAttachments = CHAT_UPLOAD_MAX_PER_MESSAGE;
    if (pendingAttachments.length + files.length > maxAttachments) {
      setErrorText(
        formatMessage(patientAgentMessages.tooManyAttachments, {
          max: maxAttachments,
        }),
      );
      return;
    }
    for (const file of files) {
      if (file.size > CHAT_UPLOAD_MAX_SIZE) {
        setErrorText(
          formatMessage(patientAgentMessages.attachFileTooLarge, {
            name: file.name,
          }),
        );
        continue;
      }
      setUploadingAttachment(true);
      // Image previews use a local object URL: the card shows the thumbnail
      // right away, while the upload is still in flight.
      const previewUrl = file.type.startsWith('image/')
        ? URL.createObjectURL(file)
        : undefined;
      const localID = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      // Admit synchronously against the ref mirror: rapid consecutive drops
      // read a stale render closure and could previously push past
      // maxAttachments.
      if (pendingAttachmentsRef.current.length >= maxAttachments) {
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
        }
        setErrorText(
          formatMessage(patientAgentMessages.tooManyAttachments, {
            max: maxAttachments,
          }),
        );
        continue;
      }
      updatePendingAttachments((current) => [
        ...current,
        {
          id: localID,
          name: file.name,
          mime: file.type,
          size: file.size,
          previewUrl,
          uploading: true,
        },
      ]);
      try {
        const attachment = await uploadChatAttachment(
          file,
          targetThreadID,
          undefined,
          currentOrganizationID || undefined,
        );
        updatePendingAttachments((current) =>
          current.map((item) =>
            item.id === localID
              ? { ...item, uploading: false, uploaded: attachment }
              : item,
          ),
        );
      } catch (reason) {
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
        }
        updatePendingAttachments((current) =>
          current.filter((item) => item.id !== localID),
        );
        setErrorText(getErrorMessage(reason, intl));
      } finally {
        setUploadingAttachment(false);
      }
    }
    setUploadingAttachment(false);
  };

  const handleAttachmentPick = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = '';
    await uploadFiles(files);
  };

  const dragCounterRef = useRef(0);
  const handleDragEnter = (event: React.DragEvent) => {
    if (!event.dataTransfer.types.includes('Files')) {
      return;
    }
    event.preventDefault();
    dragCounterRef.current += 1;
    setIsDraggingFiles(true);
  };
  const handleDragOver = (event: React.DragEvent) => {
    if (!event.dataTransfer.types.includes('Files')) {
      return;
    }
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  };
  const handleDragLeave = () => {
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
    if (dragCounterRef.current === 0) {
      setIsDraggingFiles(false);
    }
  };
  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    dragCounterRef.current = 0;
    setIsDraggingFiles(false);
    void uploadFiles(Array.from(event.dataTransfer.files ?? []));
  };
  // Screenshots live on the OS clipboard, not in the file picker: pasting an
  // image into the composer attaches it exactly like a dropped file. Plain
  // text paste is untouched — the handler returns early when no file is
  // present, so the textarea keeps its default paste behavior.
  const handlePaste = (event: React.ClipboardEvent) => {
    const files = filesFromClipboardData(event.clipboardData);
    if (!files.length) {
      return;
    }
    event.preventDefault();
    void uploadFiles(files);
  };
  const [errorText, setErrorText] = useState('');
  const [isThreadsOpen, setIsThreadsOpen] = useState(false);
  const [isResolving, setIsResolving] = useState(false);
  const [loadingThreadID, setLoadingThreadID] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [hasCollapsedCompletionNotice, setHasCollapsedCompletionNotice] =
    useState(false);
  const previousRunningRef = useRef(false);
  // Open/close is a stored preference (agentChatPreferences): the stored
  // value wins; without one the pane starts closed.
  const [isFloatingConversationOpen, setIsFloatingConversationOpen] = useState(
    () => readChatOpen() ?? false,
  );
  // Persist open/close changes. Only actual changes after mount are
  // reported: the initial value already reflects the stored preference.
  const prevFloatingOpenRef = useRef(readChatOpen() ?? false);
  useEffect(() => {
    if (prevFloatingOpenRef.current === isFloatingConversationOpen) {
      return;
    }
    prevFloatingOpenRef.current = isFloatingConversationOpen;
    writeChatOpen(isFloatingConversationOpen);
    onFloatingOpenChange?.(isFloatingConversationOpen);
  }, [isFloatingConversationOpen, onFloatingOpenChange]);
  // Floating popup size overrides, set by dragging the top or side edges.
  // Undefined values fall back to the CSS defaults.
  // Dragged floating-popup size, persisted across sessions and applied on
  // every expansion of the chat.
  const [floatingSize, setFloatingSize] = useState<AgentChatFloatingSize>(
    () => {
      if (readChatFloatingMaximized()) {
        return getMaximizedFloatingSize();
      }
      return readChatFloatingSize() ?? {};
    },
  );
  // Ref mirror of floatingSize for event handlers that outlive renders
  // (the pointerup drag handler persists the last applied size).
  const floatingSizeRef = useRef(floatingSize);
  useEffect(() => {
    floatingSizeRef.current = floatingSize;
  }, [floatingSize]);
  // Size to restore when the expand toggle is hit while expanded.
  const expandRestoreRef = useRef<AgentChatFloatingSize>({});
  const [isFloatingMaximized, setIsFloatingMaximized] = useState(
    readChatFloatingMaximized,
  );
  const docking = useChatDocking({
    paneRef: floatingFocusedRef,
    isMaximized: isFloatingMaximized,
    isOpen: floating && isFloatingConversationOpen,
    enabled: Boolean(floating),
    onDetach: (fromSideDock) => {
      if (fromSideDock || isFloatingMaximized) {
        const detached = {
          width: Math.min(760, window.innerWidth - 48),
          height: Math.min(520, window.innerHeight - 48),
        };
        setFloatingSize(detached);
        writeChatFloatingSize(detached);
      }
      setIsFloatingMaximized(false);
      writeChatFloatingMaximized(false);
      return () => {
        setFloatingSize(floatingSize);
        writeChatFloatingSize(floatingSize);
        setIsFloatingMaximized(isFloatingMaximized);
        writeChatFloatingMaximized(isFloatingMaximized);
      };
    },
  });
  // Vertical shift from the bottom anchor while dragging a top corner
  // (px the pane's bottom edge has moved down). Reset on release.
  const [verticalShift, setVerticalShift] = useState(0);
  const [isFloatingResizing, setIsFloatingResizing] = useState(false);
  // Floating sidebar (empty placeholder): static column on wide popups,
  // overlay shutter on narrow ones.
  const [isFloatingSidebarOpen, setIsFloatingSidebarOpen] = useState(false);
  const [isFloatingPaneWide, setIsFloatingPaneWide] = useState(false);
  // Dragged floating-sidebar width, persisted like the popup size.
  const [floatingSidebarWidth, setFloatingSidebarWidth] = useState(
    () => readChatSidebarWidth() ?? SIDEBAR_DEFAULT_WIDTH,
  );
  const [discardingDocumentID, setDiscardingDocumentID] = useState('');
  const [dismissedProposals, setDismissedProposals] = useState<Set<string>>(
    () => new Set(),
  );
  const [sourceAssetReportIDs, setSourceAssetReportIDs] = useState<Record<
    string,
    SourceAssetReference
  > | null>(null);

  useEffect(() => {
    if (!floating || !isFloatingConversationOpen || docking.isPinned) {
      return;
    }

    const collapseOnOutsidePointer = (event: PointerEvent) => {
      const target = event.target;
      if (
        target instanceof Node &&
        !floatingFocusedRef.current?.contains(target)
      ) {
        setIsFloatingConversationOpen(false);
        setIsThreadsOpen(false);
      }
    };

    // While an attachment preview popup is open, the popup and the chat stay
    // independent: clicks inside the popup (which lives outside this aside)
    // must not collapse the chat, and the popup itself is owned by the wrapper.
    if (attachmentPreview) {
      return;
    }

    document.addEventListener('pointerdown', collapseOnOutsidePointer);
    return () =>
      document.removeEventListener('pointerdown', collapseOnOutsidePointer);
  }, [
    attachmentPreview,
    floating,
    isFloatingConversationOpen,
    docking.isPinned,
  ]);

  // Escape collapses the focused floating chat — the keyboard counterpart of
  // the outside-pointerdown collapse. Only keys fired from inside the pane
  // count ("the chat is focused"); Escape elsewhere is left to the page.
  useEffect(() => {
    if (!floating || !isFloatingConversationOpen) {
      return;
    }

    // While an attachment preview popup is open it owns Escape (its own
    // dismissal comes first), same contract as the pointer collapse above.
    if (attachmentPreview) {
      return;
    }

    const collapseOnEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }
      const pane = floatingFocusedRef.current;
      const target = event.target;
      if (pane && target instanceof Node && pane.contains(target)) {
        setIsFloatingConversationOpen(false);
        setIsThreadsOpen(false);
      }
    };

    document.addEventListener('keydown', collapseOnEscape);
    return () => document.removeEventListener('keydown', collapseOnEscape);
  }, [attachmentPreview, floating, isFloatingConversationOpen]);

  // Re-apply the stored size whenever the chat expands; drag updates persist
  // it on release (see onUp in startFloatingResize).
  useEffect(() => {
    if (isFloatingConversationOpen) {
      if (readChatFloatingMaximized()) {
        setFloatingSize(getMaximizedFloatingSize());
        return;
      }
      const stored = readChatFloatingSize();
      if (stored) {
        setFloatingSize(clampFloatingSize(stored));
      }
    }
  }, [isFloatingConversationOpen]);

  // Re-clamp the popup size when the browser viewport changes: a stored
  // dragged size or a maximized size fit the viewport they were captured in
  // and can overflow the page after a resize.
  useEffect(() => {
    if (!floating || !isFloatingConversationOpen) {
      return;
    }
    const clampToViewport = () => {
      if (isFloatingMaximized) {
        setFloatingSize(getMaximizedFloatingSize());
        return;
      }
      setFloatingSize((current) => {
        const next = clampFloatingSize(current);
        return next.width === current.width && next.height === current.height
          ? current
          : next;
      });
    };
    clampToViewport();
    window.addEventListener('resize', clampToViewport);
    return () => window.removeEventListener('resize', clampToViewport);
  }, [floating, isFloatingConversationOpen, isFloatingMaximized]);

  // Re-clamp when the fixed bottom bar offset changes (navigating between
  // pages with and without a bar while the chat stays mounted): the pane's
  // inline height must shrink by the bar height or its top edge would poke
  // above the page top.
  useEffect(() => {
    if (!floating || !isFloatingConversationOpen) {
      return;
    }
    const root = document.documentElement;
    const observer = new MutationObserver(() => {
      setFloatingSize((current) => {
        const next = clampFloatingSize(current);
        return next.width === current.width && next.height === current.height
          ? current
          : next;
      });
    });
    observer.observe(root, { attributes: true, attributeFilter: ['style'] });
    return () => observer.disconnect();
  }, [floating, isFloatingConversationOpen]);

  // Track the popup width so the sidebar can switch between the static
  // column (wide) and the shutter (narrow) as the user resizes it. The
  // mobile layout always uses the shutter, whatever the popup width.
  useEffect(() => {
    if (!floating || !isFloatingConversationOpen) {
      return;
    }
    const pane = floatingFocusedRef.current;
    if (!pane) {
      return;
    }
    const update = () => {
      const mobileViewport = window.matchMedia(
        '(max-width: 420px), (orientation: landscape) and (max-width: 1024px)',
      ).matches;
      const wide =
        !mobileViewport && pane.offsetWidth >= FLOATING_SIDEBAR_WIDE_PX;
      setIsFloatingPaneWide(wide);
      // Switching to a wide frame auto-opens the sidebar; switching to a
      // narrow one closes it (the shutter takes user intent from there).
      setIsFloatingSidebarOpen(wide);
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(pane);
    window.addEventListener('resize', update);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [floating, isFloatingConversationOpen]);

  // (The expand-time sidebar default needs no separate effect: the width
  // observer above re-runs when the conversation opens and its initial
  // update() sets both the wide flag and the sidebar default.)

  // Selecting a thread from the sidebar in shutter mode (narrow popups,
  // where the sidebar overlays the conversation behind a backdrop) closes
  // the shutter so the picked conversation becomes visible. The wide
  // static-column sidebar stays open: it does not hide anything. Only an
  // actual change of the host thread pointer counts — re-renders with the
  // same selection (e.g. manually re-opening the shutter) keep it open.
  const prevInitialThreadIDRef = useRef(initialThreadID);
  useEffect(() => {
    const previousInitialThreadID = prevInitialThreadIDRef.current;
    prevInitialThreadIDRef.current = initialThreadID;
    if (
      !floating ||
      !initialThreadID ||
      initialThreadID === previousInitialThreadID ||
      isFloatingPaneWide ||
      !isFloatingSidebarOpen
    ) {
      return;
    }
    setIsFloatingSidebarOpen(false);
  }, [floating, initialThreadID, isFloatingPaneWide, isFloatingSidebarOpen]);

  const { dock: chatDock, setPosition: setFloatingPosition } = docking;
  const startFloatingResize = useCallback(
    (edge: 'top' | 'bottom' | 'left' | 'right' | 'tl' | 'tr' | 'bl' | 'br') =>
      (event: React.PointerEvent) => {
        if (event.button !== 0 || !event.isPrimary) {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        // Keep the resize cursor stable for the whole drag: a captured
        // pointer takes its cursor from this element, even when the moving
        // frame edge slides out from under the pointer.
        event.currentTarget.setPointerCapture(event.pointerId);
        const pane = floatingFocusedRef.current;
        if (!pane) {
          return;
        }
        const startX = event.clientX;
        const startY = event.clientY;
        const startRect = pane.getBoundingClientRect();
        const startWidth = pane.offsetWidth;
        const startHeight = pane.offsetHeight;
        const maxWidth = window.innerWidth - 48;
        // The popup bottom sits 22px above the viewport bottom; let the top
        // edge reach the very top of the page.
        const maxHeight = window.innerHeight - 22;

        const onMove = (moveEvent: PointerEvent) => {
          const deltaX = moveEvent.clientX - startX;
          const deltaY = moveEvent.clientY - startY;
          if (chatDock === 'floating' && !isFloatingMaximized) {
            const leftEdge = edge === 'left' || edge.endsWith('l');
            const rightEdge = edge === 'right' || edge.endsWith('r');
            const topEdge = edge === 'top' || edge.startsWith('t');
            const bottomEdge = edge.startsWith('b');
            const left = leftEdge
              ? Math.max(
                  0,
                  Math.min(
                    startRect.right - Math.min(360, window.innerWidth),
                    startRect.left + deltaX,
                  ),
                )
              : startRect.left;
            const right = rightEdge
              ? Math.min(
                  window.innerWidth,
                  Math.max(
                    startRect.left + Math.min(360, window.innerWidth),
                    startRect.right + deltaX,
                  ),
                )
              : startRect.right;
            const top = topEdge
              ? Math.max(
                  0,
                  Math.min(
                    startRect.bottom - Math.min(240, window.innerHeight),
                    startRect.top + deltaY,
                  ),
                )
              : startRect.top;
            const bottom = bottomEdge
              ? Math.min(
                  window.innerHeight,
                  Math.max(
                    startRect.top +
                      Math.min(240, window.innerHeight - startRect.top),
                    startRect.bottom + deltaY,
                  ),
                )
              : startRect.bottom;
            setFloatingPosition({ x: left, y: top });
            setFloatingSize({ width: right - left, height: bottom - top });
            return;
          }
          const isCorner = edge.length === 2;
          if (isCorner) {
            // Corners track the cursor 1:1: width and height change by Δ.
            // Top corners also lift the pane's bottom anchor by Δ (the top
            // edge follows the cursor; the bottom edge follows the growth).
            // The pane is center-anchored horizontally: a Δ width change
            // moves each side edge by Δ/2, so use 2Δ to keep the grabbed
            // corner tracking the cursor 1:1.
            const width = Math.min(
              maxWidth,
              Math.max(
                360,
                startWidth + 2 * deltaX * (edge.endsWith('l') ? -1 : 1),
              ),
            );
            const height = Math.min(
              maxHeight,
              Math.max(
                360,
                startHeight + deltaY * (edge.startsWith('t') ? -1 : 1),
              ),
            );
            setFloatingSize((current) => ({ ...current, width, height }));
            if (edge.startsWith('b')) {
              // Bottom corners: shift the bottom anchor down by Δ so the
              // dragged bottom edge tracks the cursor (the top edge stays).
              setVerticalShift(Math.max(0, deltaY));
            }
            return;
          }
          if (edge === 'top') {
            setFloatingSize((current) => ({
              ...current,
              height: Math.min(maxHeight, Math.max(360, startHeight - deltaY)),
            }));
          } else if (edge === 'bottom') {
            // Height follows the cursor; the bottom anchor shifts down by
            // the same amount so the dragged edge tracks it 1:1 (the top
            // edge stays). Same anchor shift the bottom corners use.
            setFloatingSize((current) => ({
              ...current,
              height: Math.min(maxHeight, Math.max(360, startHeight + deltaY)),
            }));
            setVerticalShift(Math.max(0, deltaY));
          } else {
            // The pane stays centered, so a cursor move of Δ widens each side
            // by Δ: width grows by 2Δ and the grabbed edge tracks 1:1.
            const width = Math.min(
              maxWidth,
              Math.max(
                360,
                startWidth + (edge === 'left' ? -2 * deltaX : 2 * deltaX),
              ),
            );
            setFloatingSize((current) => ({ ...current, width }));
          }
        };

        const onUp = () => {
          setIsFloatingResizing(false);
          setVerticalShift(0);
          document.removeEventListener('pointermove', onMove);
          document.removeEventListener('pointerup', onUp);
          document.removeEventListener('pointercancel', onUp);
          // Persist the dragged size for future expansions. Read from the
          // ref mirror: state updater functions must stay pure.
          writeChatFloatingSize(floatingSizeRef.current);
        };

        setIsFloatingResizing(true);
        setIsFloatingMaximized(false);
        writeChatFloatingMaximized(false);
        document.addEventListener('pointermove', onMove);
        document.addEventListener('pointerup', onUp);
        document.addEventListener('pointercancel', onUp);
      },
    [chatDock, setFloatingPosition, isFloatingMaximized],
  );

  // Drag handle on the sidebar's right edge: adjusts its width within
  // bounds and persists the value on release.
  const startSidebarResize = useCallback(
    (event: React.PointerEvent) => {
      event.preventDefault();
      // Keep the resize cursor stable for the whole drag (see
      // startFloatingResize).
      event.currentTarget.setPointerCapture(event.pointerId);
      const startX = event.clientX;
      const startWidth = floatingSidebarWidth;

      const onMove = (moveEvent: PointerEvent) => {
        const width = Math.min(
          SIDEBAR_MAX_WIDTH,
          Math.max(
            SIDEBAR_MIN_WIDTH,
            startWidth + (moveEvent.clientX - startX),
          ),
        );
        setFloatingSidebarWidth(width);
      };

      const onUp = () => {
        setIsFloatingResizing(false);
        document.removeEventListener('pointermove', onMove);
        document.removeEventListener('pointerup', onUp);
        document.removeEventListener('pointercancel', onUp);
        setFloatingSidebarWidth((current) => {
          writeChatSidebarWidth(current);
          return current;
        });
      };

      setIsFloatingResizing(true);
      document.addEventListener('pointermove', onMove);
      document.addEventListener('pointerup', onUp);
      document.addEventListener('pointercancel', onUp);
    },
    [floatingSidebarWidth],
  );

  useEffect(() => {
    if (isRunning) {
      setHasCollapsedCompletionNotice(false);
    } else if (
      previousRunningRef.current &&
      floating &&
      !isFloatingConversationOpen
    ) {
      setHasCollapsedCompletionNotice(true);
    }
    previousRunningRef.current = isRunning;
  }, [floating, isFloatingConversationOpen, isRunning]);

  // Height of the composer (including its bottom margin), published as
  // --composer-h so the viewport reserves exactly that much scrollable
  // space and the last lines can scroll above the pill. Re-runs on
  // conversation reopen: closing the floating chat unmounts the pane, so
  // the old observers die with it and the offset must be republished.
  useEffect(() => {
    if (floating && !isFloatingConversationOpen) {
      return undefined;
    }
    if (!floating && !documentMode) {
      return undefined;
    }
    const pane = paneRef.current;
    if (!pane) {
      return undefined;
    }
    // The composer can mount after the pane (form state resolves
    // asynchronously on reopen), so re-query until it exists.
    let composer = pane.querySelector<HTMLElement>('[class*=composer]');
    const publish = () => {
      if (!composer || !composer.isConnected) {
        return;
      }
      const cs = getComputedStyle(composer);
      const inner =
        composer.offsetHeight + parseFloat(cs.marginBottom || '0') || 0;
      // Skip hidden measurements (height 0) so a toggled-out composer
      // never wipes the last good offset; the resize observer publishes
      // the real height when the field becomes visible again.
      if (inner > 0) {
        // Extra breathing room between the last message and the composer,
        // matching the viewport's other element-boundary spacings.
        const BOTTOM_GAP = 20;
        pane.style.setProperty('--composer-h', `${inner + BOTTOM_GAP}px`);
      }
    };
    let composerObserver: ResizeObserver | undefined;
    const observeComposer = () => {
      if (!composer) {
        return;
      }
      publish();
      composerObserver = new ResizeObserver(publish);
      composerObserver.observe(composer);
    };
    const ensureComposer = () => {
      // The slot element can be swapped (e.g. the approval gate replaces the
      // composer while sending is blocked), so a detached reference must not
      // stick — re-query until a connected element is found.
      if (composer && composer.isConnected) {
        return;
      }
      composer = pane.querySelector<HTMLElement>('[class*=composer]');
      if (composer) {
        composerObserver?.disconnect();
        observeComposer();
      }
    };
    // The composer can already exist at effect time (document-mode rail);
    // publish immediately, otherwise fall back to the mutation-driven path.
    if (composer) {
      observeComposer();
    } else {
      ensureComposer();
    }
    const mutation = new MutationObserver(ensureComposer);
    mutation.observe(pane, { childList: true, subtree: true });
    return () => {
      mutation.disconnect();
      composerObserver?.disconnect();
    };
  }, [floating, documentMode, isFloatingConversationOpen, isThreadsOpen]);

  const dispatch = useAppDispatch();
  const patient = useAppSelector(
    patientModel.selectors.selectPatientByID(patientID),
  );
  // The shell mounts outside the patient page, where the store may not hold
  // the patient entity yet; the agent's own resolve response has the name.
  const resolvedPatientName = resolved?.patient?.patient
    ? [resolved.patient.patient.firstName, resolved.patient.patient.lastName]
        .filter(Boolean)
        .join(' ')
    : '';
  const patientDisplayName =
    [patient?.PersonalData?.FirstName, patient?.PersonalData?.LastName]
      .filter(Boolean)
      .join(' ') || resolvedPatientName;

  const language = useAppSelector(userModel.selectors.selectUserLocale);
  const patientOrganization = useAppSelector(
    organizationModel.selectors.selectPatientOrganization,
  );
  const reports = useAppSelector(reportsModel.selectors.selectAll);
  // Full display name of the bound entity for the welcome hero: report
  // type + creation date, the host-provided document title, or the
  // encounter name; falls back to nothing (the hero then uses the patient
  // name only).
  const subjectReport =
    scopeSubject?.type === 'report'
      ? reports.find((item) => item.ID === scopeSubject.id)
      : undefined;
  const reportEntityName = subjectReport
    ? getReportContextLabel(subjectReport)
    : '';
  const subjectEncounter = useAppSelector(
    encounterModel.selectors.selectEncounterById(
      scopeSubject?.type === 'encounter' ? scopeSubject.id : undefined,
    ),
  );
  const scopeEntityName =
    scopeSubject?.type === 'report'
      ? reportEntityName || undefined
      : scopeSubject?.type === 'document'
        ? documentName || undefined
        : scopeSubject?.type === 'encounter'
          ? subjectEncounter?.name || undefined
          : undefined;
  const threadEntityName = isGeneralDraft ? undefined : scopeEntityName;
  const navigateToReport = useNavigateToReport();
  const navigate = useNavigate();
  const location = useLocation();
  const currentChatURL = `${window.location.origin}${location.pathname}${location.search}`;

  const hasAssetReference = threadItems.some(
    (item) =>
      item.type === 'message' && item.content.includes('](/agent-ref/asset/'),
  );

  useEffect(() => {
    if (!hasAssetReference || sourceAssetReportIDs !== null) {
      return;
    }

    let active = true;
    void api.patientDocument
      .listPatientDocumentSourceAssets(
        new ListPatientDocumentSourceAssetsReq({ PatientID: patientID }),
      )
      .then(async (response) => {
        if (!active) {
          return;
        }
        const sources = response.Assets.filter((source) => source.Asset?.ID);
        const entries: [string, SourceAssetReference][] = [];
        const batchSize = 64;
        for (let offset = 0; offset < sources.length; offset += batchSize) {
          if (!active) {
            return;
          }
          const batch = await Promise.all(
            sources.slice(offset, offset + batchSize).map(async (source) => {
              const assetID = source.Asset?.ID || '';
              return [
                await createAssetReferenceID(assetID),
                { assetID, reportID: source.ReportID },
              ] as [string, SourceAssetReference];
            }),
          );
          entries.push(...batch);
        }
        if (active) {
          setSourceAssetReportIDs(Object.fromEntries(entries));
        }
      })
      .catch(() => {
        if (active) {
          setSourceAssetReportIDs({});
        }
      });

    return () => {
      active = false;
    };
  }, [hasAssetReference, patientID, sourceAssetReportIDs]);

  // The onboarding flow renderer turns the agent's ```onboarding fenced
  // blocks into interactive tour cards instead of code blocks.
  const streamdownPlugins = useMemo<StreamdownPluginConfig>(
    () => ({
      renderers: [
        {
          language: 'onboarding',
          component: makeOnboardingRenderer((flow) => setOnboardingFlow(flow)),
        },
      ],
    }),
    [],
  );

  const citationComponents = useMemo<StreamdownComponents>(
    () => ({
      ...streamdownComponents,
      a: ({ children, href, ...props }) => {
        const reference = parseEvidenceReference(href);
        if (!reference) {
          return (
            <a {...props} href={stripChatQuery(href, currentChatURL)}>
              {children}
            </a>
          );
        }

        const reportID =
          reference.kind === 'report' ? reference.id : reference.reportID;
        const report = reports.find((item) => item.ID === reportID);
        const sourceAsset = sourceAssetReportIDs?.[reference.id];
        const isKnownAsset =
          reference.kind === 'asset' &&
          sourceAsset?.reportID === reference.reportID;
        const canNavigate =
          reference.kind === 'study' ||
          (reference.kind === 'report' && Boolean(report)) ||
          (reference.kind === 'asset' && isKnownAsset && Boolean(report)) ||
          reference.kind === 'document';

        const label =
          reference.kind === 'report' && report
            ? getReportContextLabel(report)
            : children;

        return (
          <span className={styles.evidenceReference}>
            <button
              className={styles.evidenceLink}
              disabled={!canNavigate}
              type="button"
              onClick={() => {
                if (reference.kind === 'study') {
                  void api.study
                    .getStudyViewerData({ StudyID: reference.id })
                    .then(({ Study }) => {
                      if (!Study?.PatientID) return;
                      window.dispatchEvent(
                        new CustomEvent('diagnocat:open-study', {
                          detail: reference,
                        }),
                      );
                      navigate(
                        stripChatQuery(
                          `/patients/${Study.PatientID}`,
                          currentChatURL,
                        ),
                      );
                    })
                    .catch((reason) =>
                      setErrorText(getErrorMessage(reason, intl)),
                    );
                  return;
                }
                if (reference.kind === 'document') {
                  // Opens in the patient documents widget via the profile.
                  Promise.resolve(onOpenDocument?.(reference.id)).catch(
                    (reason) => setErrorText(getErrorMessage(reason, intl)),
                  );
                  return;
                }
                if (report) {
                  void navigateToReport({
                    reportID: report.ID,
                    reportType: report.Type,
                    patientID: report.PatientID,
                    CBCTOrthoPDFUrl: '',
                    search: new URL(
                      stripChatQuery('/', currentChatURL),
                      window.location.origin,
                    ).search,
                  });
                }
              }}
            >
              {label}
            </button>
            <span className={styles.evidencePreview} role="tooltip">
              {isKnownAsset && sourceAsset ? (
                <img
                  alt=""
                  src={getImageSrc(sourceAsset.assetID, 'thumbnail')}
                />
              ) : null}
              <span>{label}</span>
            </span>
          </span>
        );
      },
    }),
    [
      currentChatURL,
      navigateToReport,
      onOpenDocument,
      reports,
      sourceAssetReportIDs,
      intl,
      navigate,
    ],
  );

  const organizationID =
    patient?.OrganizationID ||
    patientOrganization?.ID ||
    currentOrganizationID ||
    '';
  const isContextReady = Boolean(currentUser.ID && currentOrganizationID);

  const getActor = useCallback(
    () => buildActor(currentUser.ID, language),
    [currentUser.ID, language],
  );

  const getActiveContext = useCallback(
    () =>
      resolved && !isGeneralDraft
        ? getCanonicalContext(patientID, organizationID)
        : {
            // General drafts create and list patient-less threads.
            patient_id: isGeneralDraft ? '' : patientID,
            organization_id: organizationID,
          },
    [organizationID, patientID, resolved, isGeneralDraft],
  );

  const loadThreads = useCallback(async () => {
    if (!isContextReady || !currentUser.ID) {
      return;
    }

    try {
      const nextThreads = await listAgentCoreThreads(getActor(), {
        organization_id: currentOrganizationID,
      });
      setThreads(nextThreads);
    } catch (error) {
      // Errors surface as an empty list; no inline banner in floating mode.
    }
  }, [currentUser.ID, currentOrganizationID, getActor, isContextReady]);

  useEffect(
    () => () => {
      abortControllerRef.current?.abort();
      threadLoadCoordinatorRef.current.invalidate();
    },
    [],
  );

  useEffect(() => {
    setResolved(null);
    setSourceAssetReportIDs(null);
    setIsResolving(Boolean(patientID));
  }, [patientID]);

  useEffect(() => {
    if (!patientID || resolved) {
      return;
    }

    let active = true;

    const loadContext = async () => {
      setIsResolving(true);
      setErrorText('');

      try {
        const requestedContext = {
          patient_id: patientID,
          organization_id: organizationID,
        };

        let response = await resolveAgentCoreContext(
          buildActor(currentUser.ID, language),
          requestedContext,
        );

        if (!response.patient && requestedContext.organization_id) {
          response = await resolveAgentCoreContext(
            buildActor(currentUser.ID, language),
            {
              patient_id: patientID,
            },
          );
        }

        if (!active) {
          return;
        }

        setResolved(response);
      } catch (error) {
        if (organizationID && isOrganizationMismatchError(error)) {
          try {
            const retryResponse = await resolveAgentCoreContext(
              buildActor(currentUser.ID, language),
              {
                patient_id: patientID,
              },
            );

            if (!active) {
              return;
            }

            setResolved(retryResponse);
            return;
          } catch (retryError) {
            if (!active) {
              return;
            }

            setErrorText(getErrorMessage(retryError, intl));
            return;
          }
        }

        if (!active) {
          return;
        }

        setErrorText(getErrorMessage(error, intl));
      } finally {
        if (active) {
          setIsResolving(false);
        }
      }
    };

    void loadContext();

    return () => {
      active = false;
    };
  }, [
    currentUser.ID,
    intl,
    language,
    organizationID,
    patientID,
    resolved,
    setThreadItemsFor,
  ]);

  useEffect(() => {
    if (isContextReady) void loadThreads();
  }, [isContextReady, loadThreads]);

  const previousRequestedThread = useRef(initialThreadID);
  useEffect(() => {
    const previous = previousRequestedThread.current;
    previousRequestedThread.current = initialThreadID;
    if (previous && !initialThreadID && threadID) {
      threadLoadCoordinatorRef.current.invalidate();
      selectThread('');
      setThreadItemsFor(DRAFT_THREAD_KEY, []);
      setLoadingThreadID('');
    }
  }, [initialThreadID, selectThread, setThreadItemsFor, threadID]);

  // Thread loads have two request sources: the host channel (sidebar
  // selection mirrored back through initialThreadID) and the stored pointer
  // (restoreTarget — set at mount/scope change). A failed load leaves the
  // request unmet and the pointer untouched; the pointer is replaced only
  // when the user selects or creates another thread.
  useEffect(() => {
    const requestedThreadID = initialThreadID || restoreTarget;
    if (
      !requestedThreadID ||
      !isContextReady ||
      isRunning ||
      threadID === requestedThreadID
    ) {
      return;
    }

    let active = true;
    const loadGeneration = threadLoadCoordinatorRef.current.begin();
    setLoadingThreadID(requestedThreadID);
    setErrorText('');

    void getAgentCoreThread(
      getActor(),
      { organization_id: currentOrganizationID },
      requestedThreadID,
    )
      .then((detail) => {
        if (
          !active ||
          !threadLoadCoordinatorRef.current.isCurrent(loadGeneration)
        ) {
          return;
        }
        const resolvedThreadID = detail.thread?.id || requestedThreadID;
        setLoadedThread(detail.thread ?? null);
        setLoadingThreadID('');
        // Only explicit selection or restoration changes the conversation.
        activateThread(resolvedThreadID);
        setThreadItemsFor(
          resolvedThreadID,
          toThreadItems(resolvedThreadID, detail.messages, intl.formatMessage),
        );
        // Persist the previous conversation draft before adopting this one.
        flushPendingDraft();
      })
      .catch((error) => {
        if (
          active &&
          threadLoadCoordinatorRef.current.isCurrent(loadGeneration)
        ) {
          setLoadingThreadID('');
          setErrorText(getErrorMessage(error, intl));
        }
      });

    return () => {
      active = false;
    };
  }, [
    activateThread,
    flushPendingDraft,
    getActor,
    currentOrganizationID,
    initialThreadID,
    intl,
    isRunning,
    isContextReady,
    restoreTarget,
    setThreadItemsFor,
    threadID,
  ]);

  useLayoutEffect(() => {
    isAtBottomRef.current = true;
    setShowScrollToBottom(false);
  }, [activeThreadKey, isFloatingConversationOpen]);

  useLayoutEffect(() => {
    if (!isFloatingConversationOpen || !chatViewportRef.current) {
      return;
    }

    const scrollToEnd = () => {
      const element = chatViewportRef.current;
      if (!element) {
        return;
      }

      if (!isAtBottomRef.current) {
        return;
      }

      if (!threadItems.length && !errorText && !isRunning) {
        element.scrollTop = 0;
        return;
      }

      element.scrollTop = element.scrollHeight;
      setShowScrollToBottom(false);
    };
    const frameID = window.requestAnimationFrame(scrollToEnd);
    // The floating surface changes height over 200ms when entering or leaving
    // full-screen mode. Re-pin after that transition so the final message and
    // follow-up controls cannot settle underneath the composer.
    const settleTimerID = window.setTimeout(scrollToEnd, 220);

    return () => {
      window.cancelAnimationFrame(frameID);
      window.clearTimeout(settleTimerID);
    };
  }, [
    activeThreadKey,
    errorText,
    isFloatingConversationOpen,
    isResolving,
    isRunning,
    threadItems,
  ]);

  // After a run completes, replace the optimistic transcript (local IDs, no
  // server metadata) with the persisted thread, so message IDs, timestamps
  // and author IDs match what a page refresh would show.
  const reconcileThreadAfterRun = useCallback(
    async (
      runContext: AgentCoreContext,
      targetThreadID: string,
      runSequence: number,
    ) => {
      if (!targetThreadID) {
        return;
      }
      try {
        const detail = await getAgentCoreThread(
          getActor(),
          runContext,
          targetThreadID,
        );
        if (runSequence !== runSequenceRef.current) {
          return;
        }
        setThreadItemsFor(
          targetThreadID,
          toThreadItems(targetThreadID, detail.messages, formatMessage),
        );
        // Recovery path: the live completion event may never have fired, so
        // the reconcile is this run's last chance to auto-start the tour.
        let lastAssistantContent = '';
        for (let i = detail.messages.length - 1; i >= 0; i--) {
          const message = detail.messages[i];
          if (message.role === 'assistant' && 'content' in message) {
            lastAssistantContent = message.content;
            break;
          }
        }
        if (lastAssistantContent) {
          maybeAutoStartOnboarding(lastAssistantContent);
        }
      } catch {
        // Best-effort: the optimistic transcript stays until the next load.
      }
    },
    [getActor, setThreadItemsFor, maybeAutoStartOnboarding],
  );

  // Collapsed resting-bar copy inputs; pure derivations so they can be
  // memoized above the early returns (hooks must not follow conditionals).
  const collapsedPreviewItems = useMemo(
    () => toCollapsedPreviewItems(threadItems),
    [threadItems],
  );
  // Hoisted above the early return: hooks must not follow conditionals,
  // and the background thread-switch memory below must keep updating in
  // every render state.
  const isLoadingActiveThread =
    Boolean(loadingThreadID) &&
    (loadingThreadID === threadID || threadID.length === 0);

  if ((isMobile && !documentMode && !floating) || !currentUser.ID) {
    return null;
  }

  const collapsedPreview = getCollapsedThreadPreview(collapsedPreviewItems);
  const collapsedRunningEvent = getLatestRunningEvent(collapsedPreviewItems);

  // The latest pending proposal gates sending: the composer is replaced by
  // the approval widget until a decision is made.
  const pendingApproval = [...threadItems]
    .reverse()
    .find(
      (item): item is ThreadApprovalItem =>
        item.type === 'approval' && item.status === 'pending',
    );

  const handleApprovalDecision = (
    proposalID: string,
    decision: 'approve' | 'disapprove' | 'feedback',
    decisionFeedback?: string,
  ) => {
    const decisionMessage =
      decision === 'approve'
        ? formatMessage(patientAgentMessages.approvalApproved)
        : decision === 'disapprove'
          ? formatMessage(patientAgentMessages.approvalDisapproved)
          : decisionFeedback || '';
    void sendPrompt(decisionMessage, { proposalID, decision });
  };

  const sendPrompt = async (prompt: string, approval?: AgentCoreApproval) => {
    const trimmedPrompt = prompt.trim();
    if (
      !trimmedPrompt ||
      ((initialThreadID || restoreTarget) &&
        (initialThreadID || restoreTarget) !== threadID) ||
      isRunning ||
      (!isGeneralDraft && isResolving) ||
      (pendingApproval && !approval)
    ) {
      return;
    }
    // The send button is disabled while an attachment upload is in flight,
    // but Enter bypassed that guard and silently dropped the in-flight
    // attachment (its card and preview were revoked below).
    if (pendingAttachmentsRef.current.some((item) => item.uploading)) {
      setErrorText(
        formatMessage(patientAgentMessages.attachmentStillUploading),
      );
      return;
    }

    const activeThread = threads.find((thread) => thread.id === threadID);
    if (activeThread && activeThread.status === 'archived') {
      setErrorText(formatMessage(patientAgentMessages.threadArchived));
      return;
    }

    const runSequence = ++runSequenceRef.current;
    onboardingAutoStartDoneRef.current = false;
    const actor = getActor();
    const nextContext = getActiveContext();
    const submittedPageContext =
      isGeneralDraft && patientID ? undefined : pageContext;

    const readyAttachments = pendingAttachments.flatMap((item) =>
      item.uploaded ? [item.uploaded] : [],
    );

    // The sender is known at send time; stamping it lets the optimistic
    // message show the author label before the server confirms the thread.
    const userMessage: ThreadItem = {
      id: createMessageID('user'),
      type: 'message',
      role: 'user',
      content: trimmedPrompt,
      authorID: currentUser.ID,
      createdAt: new Date().toISOString(),
      attachments: readyAttachments,
      messageContext: {
        ...nextContext,
        label: isGeneralDraft ? undefined : patientDisplayName,
        resources: submittedPageContext?.resources ?? [],
      },
    };
    const assistantMessageID = createMessageID('assistant');
    const responseStartedAt = Date.now();
    const assistantMessage: ThreadItem = {
      id: assistantMessageID,
      type: 'message',
      role: 'assistant',
      content: '',
      selectionDocumentID: activeEditorKey,
      selectionContext:
        !isGeneralDraft && documentMode && selectedDocumentText
          ? selectedDocumentText
          : undefined,
    };
    let assistantMessageStarted = false;
    let activeToolCalls = 0;
    let thinkingItemID = '';
    const startThinking = () => {
      thinkingItemID = createMessageID('thinking');
      setThreadItems((currentItems) => [
        ...currentItems,
        {
          id: thinkingItemID,
          type: 'event',
          tone: 'running',
          label: formatMessage(patientAgentMessages.thinking),
          visual: 'progress',
        },
      ]);
    };

    const completeThinking = () => {
      if (!thinkingItemID) {
        return;
      }

      const completedID = thinkingItemID;
      thinkingItemID = '';

      setThreadItems((currentItems) =>
        currentItems.filter((item) => item.id !== completedID),
      );
    };

    const attachments = readyAttachments;
    setErrorText('');
    setIsRunning(true);
    setInputValue('');
    pendingAttachments.forEach((item) => {
      if (item.previewUrl) {
        URL.revokeObjectURL(item.previewUrl);
      }
    });
    pendingAttachmentsRef.current = [];
    setPendingAttachments([]);
    setThreadItems((currentItems) => [
      ...currentItems,
      userMessage,
      assistantMessage,
    ]);
    startThinking();

    const controller = new AbortController();
    abortControllerRef.current = controller;
    let runThreadID = threadID;
    let hasRunEvidence = false;
    let hasPersistedPrompt = false;
    const persistedPromptCountBeforeRun = threadItems.filter(
      (item) =>
        item.type === 'message' &&
        item.role === 'user' &&
        item.content.trim() === trimmedPrompt,
    ).length;
    const threadIDsBeforeRun = new Set(threads.map((item) => item.id));
    const adoptRunThread = (nextThreadID: string) => {
      activateThread(nextThreadID, true);
    };

    try {
      await streamAgentCoreRun({
        actor,
        context: nextContext,
        message: trimmedPrompt,
        attachments,
        pageContext: submittedPageContext
          ? { ...submittedPageContext, dom: serializePageDom() }
          : undefined,
        approval,
        threadID,
        subject: threadSubject,
        signal: controller.signal,
        onEvent: (event: AgentCoreRunEvent) => {
          hasRunEvidence = true;
          if (event.type === 'started') {
            runThreadID = getStringValue(event.thread_id);
            adoptRunThread(runThreadID);
            if (approval) {
              setThreadItems((currentItems) =>
                currentItems.map((item) =>
                  item.type === 'approval' &&
                  item.proposalID === approval.proposalID
                    ? {
                        ...item,
                        status:
                          approval.decision === 'approve'
                            ? 'approved'
                            : approval.decision === 'disapprove'
                              ? 'disapproved'
                              : 'feedback',
                      }
                    : item,
                ),
              );
            }
            return;
          }

          if (event.type === 'message_delta') {
            const content = getStringValue(event.content);
            if (!content) {
              return;
            }

            completeThinking();
            assistantMessageStarted = true;

            setThreadItems((currentItems) =>
              updateThreadMessageAtEnd(
                currentItems,
                assistantMessageID,
                (message) => ({
                  ...message,
                  content: message.content + content,
                }),
              ),
            );
            return;
          }

          if (event.type === 'message_completed') {
            const content = getStringValue(event.content);
            completeThinking();
            if (content) {
              assistantMessageStarted = true;
            }
            // A valid onboarding flow in the completed answer auto-starts the
            // tour once; the flow card in the message keeps a replay button.
            if (content) {
              maybeAutoStartOnboarding(content);
            }

            setThreadItems((currentItems) =>
              updateThreadMessageAtEnd(
                currentItems,
                assistantMessageID,
                (message) => ({
                  ...message,
                  content,
                }),
              ),
            );
            return;
          }

          if (event.type === 'activity') {
            completeThinking();
            const activity = event.activity;
            activeToolCalls += activity.state === 'running' ? 1 : -1;
            activeToolCalls = Math.max(0, activeToolCalls);
            const summary = presentActivity(activity, formatMessage);
            setThreadItems((currentItems) =>
              upsertThreadEvent(currentItems, {
                id: activity.id || createMessageID('activity'),
                tone:
                  activity.state === 'completed'
                    ? 'done'
                    : activity.state === 'failed'
                      ? 'error'
                      : 'running',
                label: summary.label,
                completedAt:
                  activity.state === 'running' ? undefined : Date.now(),
                detail: summary.detail,
                startedAt: responseStartedAt,
                visual: summary.visual,
              }),
            );
            if (
              activeToolCalls === 0 &&
              !assistantMessageStarted &&
              !thinkingItemID
            ) {
              startThinking();
            }
            return;
          }

          if (event.type === 'artifact') {
            const artifact = event.artifact;
            if ('proposalID' in artifact) {
              setThreadItems((currentItems) => [
                ...currentItems,
                {
                  id: artifact.artifactID || createMessageID('approval'),
                  type: 'approval',
                  proposalID: artifact.proposalID,
                  kind: artifact.kind,
                  title: artifact.title,
                  preview: artifact.preview,
                  status: 'pending',
                },
              ]);
              return;
            }
            if ('resourceID' in artifact) {
              // Applied Transcriptor catalog change: refresh the store so the
              // open page re-renders, and surface a created resource in its
              // editor.
              if (artifact.kind === 'template') {
                if (currentOrganizationID) {
                  void dispatch(
                    templatesModel.fetchDocumentTemplates({
                      organization_id: currentOrganizationID,
                    }),
                  );
                  void dispatch(
                    templatesModel.fetchDeletedDocumentTemplates({
                      organization_id: currentOrganizationID,
                    }),
                  );
                }
              } else if (currentOrganizationID) {
                void dispatch(
                  templatesModel.fetchEncounterSchemas({
                    organization_id: currentOrganizationID,
                    include_draft: true,
                  }),
                );
              }
              if (artifact.action === 'created') {
                navigate(
                  buildTemplatesUrl(
                    artifact.kind === 'template'
                      ? { editor: 'document', id: artifact.resourceID }
                      : { editor: 'schema', id: artifact.resourceID },
                  ),
                );
              }
              return;
            }
            onDocumentChanged?.(artifact.documentID);
            setThreadItems((currentItems) =>
              upsertThreadDocument(currentItems, {
                id: artifact.artifactID || createMessageID('document'),
                type: 'document',
                documentID: artifact.documentID,
                title: artifact.title,
                action: artifact.action,
                assetCount: artifact.assetCount,
                revision: artifact.revision,
              }),
            );
            return;
          }

          if (event.type === 'completed') {
            completeThinking();
            if (event.thread_id) {
              runThreadID = getStringValue(event.thread_id);
              adoptRunThread(runThreadID);
            }
            setThreadItems((currentItems) =>
              currentItems.map((item) =>
                item.type === 'event' && item.tone === 'running'
                  ? {
                      ...item,
                      tone: 'done',
                      visual:
                        item.visual === 'progress' ? 'check' : item.visual,
                    }
                  : item,
              ),
            );
            if (runThreadID && !approval) {
              void reconcileThreadAfterRun(
                nextContext,
                runThreadID,
                runSequence,
              );
            }
            return;
          }

          if (event.type === 'failed') {
            completeThinking();
            setThreadItems((currentItems) => [
              ...currentItems,
              {
                id: createMessageID('error'),
                type: 'event',
                tone: 'error',
                label:
                  getStringValue(event.code) ||
                  formatMessage(patientAgentMessages.eventFailed),
                detail:
                  getStringValue(event.message) ||
                  formatMessage(patientAgentMessages.agentRunFailed),
                visual: 'error',
              },
            ]);
          }
        },
      });
    } catch {
      if (controller.signal.aborted) {
        completeThinking();
        return;
      }

      // A broken response stream can happen before or after Restate accepts the
      // run. Reconcile silently while there is evidence of a durable run. If no
      // event or persisted prompt appears, stop after a bounded acceptance
      // window instead of polling forever for a run that never started.
      const recoveryStartedAt = Date.now();
      while (!controller.signal.aborted) {
        await waitFor(2000);
        try {
          if (!runThreadID) {
            const nextThreads = await listAgentCoreThreads(actor, nextContext);
            for (const candidate of nextThreads) {
              if (threadIDsBeforeRun.has(candidate.id)) {
                continue;
              }
              const candidateDetail = await getAgentCoreThread(
                actor,
                nextContext,
                candidate.id,
              );
              if (hasUserPrompt(candidateDetail.messages, trimmedPrompt)) {
                hasPersistedPrompt = true;
                runThreadID = candidate.id;
                adoptRunThread(runThreadID);
                break;
              }
            }
          }

          if (runThreadID) {
            const detail = await getAgentCoreThread(
              actor,
              nextContext,
              runThreadID,
            );
            hasPersistedPrompt =
              hasPersistedPrompt ||
              countUserPrompts(detail.messages, trimmedPrompt) >
                persistedPromptCountBeforeRun;

            if (hasCompletedTurnAfterPrompt(detail.messages, trimmedPrompt)) {
              const recoveredThreadID = detail.thread?.id || runThreadID;
              const recoveredItems = toThreadItems(
                recoveredThreadID,
                detail.messages,
                formatMessage,
              );
              activateThread(recoveredThreadID);
              setThreadItemsFor(recoveredThreadID, recoveredItems);
              setErrorText('');
              recoveredItems.forEach((item) => {
                if (item.type === 'document') {
                  onDocumentChanged?.(item.documentID);
                }
              });
              completeThinking();
              return;
            }
          }
        } catch {
          // Restate, the service, or the network may still be recovering.
        }

        const elapsedMs = Date.now() - recoveryStartedAt;
        if (
          shouldStopUnacceptedRecovery({
            hasRunEvidence,
            hasPersistedPrompt,
            elapsedMs,
          }) ||
          hasExceededRecoveryWindow(elapsedMs)
        ) {
          completeThinking();
          const message = formatMessage(patientAgentMessages.runNotStarted);
          setErrorText(message);
          setThreadItems((currentItems) => [
            ...currentItems.filter(
              (item) =>
                item.id !== assistantMessageID ||
                item.type !== 'message' ||
                Boolean(item.content),
            ),
            {
              id: createMessageID('error'),
              type: 'event',
              tone: 'error',
              label: formatMessage(patientAgentMessages.runNotStartedTitle),
              detail: message,
              visual: 'error',
            },
          ]);
          return;
        }
      }

      completeThinking();
      return;
    } finally {
      if (approval && runThreadID) {
        await reconcileThreadAfterRun(nextContext, runThreadID, runSequence);
      }
      setIsRunning(false);
      abortControllerRef.current = null;
      void loadThreads();
    }
  };

  const handleComposerKeyDown: React.KeyboardEventHandler<
    HTMLTextAreaElement
  > = (event) => {
    const keyboardEvent = {
      key: event.key,
      shiftKey: event.shiftKey,
      altKey: event.altKey,
      isComposing: event.nativeEvent.isComposing,
    };

    if (shouldInsertComposerNewline(keyboardEvent)) {
      event.preventDefault();
      const textarea = event.currentTarget;
      const selectionStart = textarea.selectionStart;
      const selectionEnd = textarea.selectionEnd;
      const nextValue = `${inputValue.slice(0, selectionStart)}\n${inputValue.slice(selectionEnd)}`;
      const nextCaret = selectionStart + 1;

      setInputValue(nextValue);
      window.requestAnimationFrame(() => {
        textarea.setSelectionRange(nextCaret, nextCaret);
      });
      return;
    }

    if (!shouldSubmitComposer(keyboardEvent)) {
      return;
    }

    event.preventDefault();
    if (isResolving || isRunning || pendingApproval || !inputValue.trim()) {
      return;
    }
    void sendPrompt(inputValue);
  };

  const insertPrompt = (prompt: string) => {
    setInputValue(trimInsertedPrompt(prompt));
  };

  const resetConversation = () => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    threadLoadCoordinatorRef.current.invalidate();
    flushPendingDraft();
    writeChatSubjectDraft(`${workspaceKey}:${DRAFT_THREAD_KEY}`, '');
    setInputValue('');
    pendingAttachmentsRef.current.forEach((item) => {
      if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
    });
    pendingAttachmentsRef.current = [];
    setPendingAttachments([]);
    setErrorText('');
    setIsRunning(false);
    setThreadItemsFor(DRAFT_THREAD_KEY, []);
    activeThreadKeyRef.current = DRAFT_THREAD_KEY;
    selectThread('');
    onThreadChanged?.('');
    setIsThreadsOpen(false);
  };

  const discardDocument = async (documentID: string) => {
    if (!documentID || discardingDocumentID) {
      return;
    }

    setDiscardingDocumentID(documentID);
    setErrorText('');
    try {
      await api.patientDocument.deletePatientDocument(
        new DeletePatientDocumentReq({ DocumentID: documentID }),
      );
      setThreadItems((currentItems) =>
        currentItems.filter(
          (item) => item.type !== 'document' || item.documentID !== documentID,
        ),
      );
      onDocumentChanged?.(documentID);
    } catch (error) {
      setErrorText(getErrorMessage(error, intl));
    } finally {
      setDiscardingDocumentID('');
    }
  };

  const subtitle = isResolving
    ? formatMessage(patientAgentMessages.subtitleLoading)
    : '';
  const toggleLabel = isThreadsOpen
    ? formatMessage(patientAgentMessages.toggleShowChat)
    : formatMessage(patientAgentMessages.toggleShowSessions);
  const { writePresets, understandPresets } = buildStarterPrompts(
    formatMessage,
    pageContext,
  );
  const firstWorkEventID = threadItems.find(
    (item) => item.type === 'event' && !item.id.startsWith('thinking'),
  )?.id;
  const requestedThreadID = initialThreadID || restoreTarget;
  const displayedThreadItems =
    requestedThreadID && requestedThreadID !== threadID
      ? threadItemsByThreadID[requestedThreadID] ?? EMPTY_THREAD_ITEMS
      : threadItems;
  // A thread was requested (host channel or stored pointer) but its
  // history has not loaded yet — covers the mount window before the
  // patient context resolves, so the welcome/new-thread state never
  // flashes.
  const isInitialThreadPending =
    Boolean(requestedThreadID) && threadID !== requestedThreadID;
  const threadLoading = isLoadingActiveThread || isInitialThreadPending;
  const collapsedStatus = isRunning
    ? collapsedRunningEvent?.label ||
      (collapsedPreview?.kind === 'assistant'
        ? formatMessage(patientAgentMessages.collapsedWriting)
        : formatMessage(patientAgentMessages.collapsedWorking))
    : hasCollapsedCompletionNotice
      ? formatMessage(patientAgentMessages.collapsedReady)
      : collapsedPreview?.kind === 'assistant'
        ? formatMessage(patientAgentMessages.collapsedLastResponse)
        : collapsedPreview
          ? formatMessage(patientAgentMessages.collapsedLatestActivity)
          : '';
  const collapsedDetail = isRunning
    ? collapsedRunningEvent?.detail || collapsedPreview?.text
    : collapsedPreview?.text;

  // The tour is an independent body-portal overlay: it must stay mounted
  // regardless of the chat view state (expanded pane, collapsed resting bar,
  // document-mode rail), so it is rendered alongside every return path.
  const onboardingTour = onboardingFlow ? (
    <OnboardingTour flow={onboardingFlow} onClose={closeOnboardingFlow} />
  ) : null;

  if (floating && !isFloatingConversationOpen) {
    // Idle collapse shrinks to a compact round launcher; a completion notice
    // expands it into the full notification bar with a pulsing shadow.
    const showNotice = hasCollapsedCompletionNotice;
    return (
      <>
        {createPortal(
          <aside
            key="restingBar"
            style={docking.triggerStyle}
            data-dock={docking.dock}
            className={cn(
              styles.agentChatRestingBar,
              !showNotice && styles.agentChatRestingBarCompact,
              isRunning &&
                cn(
                  styles.agentChatRestingBarRunning,
                  styles.agentChatRestingBarWorking,
                ),
              showNotice &&
                cn(
                  styles.agentChatRestingBarReady,
                  styles.agentChatRestingBarNotice,
                ),
              className,
            )}
            aria-label={formatMessage(patientAgentMessages.ariaLabelChat)}
            data-section="agent-chat"
          >
            <button
              type="button"
              className={styles.agentChatPromptButton}
              data-component="open-agent-chat-button"
              onClick={() => {
                setHasCollapsedCompletionNotice(false);
                setIsFloatingConversationOpen(true);
              }}
              aria-label={formatMessage(patientAgentMessages.openAgent)}
            >
              <span className={styles.agentChatMark}>
                <FloatingAgentMark />
              </span>
              {showNotice && collapsedStatus ? (
                <span
                  className={styles.agentChatCollapsedCopy}
                  role="status"
                  aria-live="polite"
                >
                  <span className={styles.agentChatCollapsedStatusRow}>
                    <strong>{collapsedStatus}</strong>
                  </span>
                  {collapsedDetail ? <span>{collapsedDetail}</span> : null}
                </span>
              ) : null}
            </button>
          </aside>,
          document.body,
        )}
        {onboardingTour}
      </>
    );
  }

  // The shell composer is always pinned to the bottom; the welcome content
  // is centered in the free space above it (no inline placement).

  // Icon-only new chat trigger, styled like the sidebar toggle. It lives in
  // the pane header while the sidebar is closed and jumps into the sidebar
  // header (right-aligned, same row as the toggle) once it is expanded.
  const newChatTrigger = (
    <button
      type="button"
      className={styles.agentChatSidebarTrigger}
      aria-label={formatMessage(patientAgentMessages.newChat)}
      title={formatMessage(patientAgentMessages.newChat)}
      onClick={resetConversation}
      disabled={isRunning || isResolving || uploadingAttachment}
    >
      <Icon name="plus" size={20} />
    </button>
  );

  const chatPane = (
    <aside
      key="chatPane"
      data-dock={floating ? docking.dock : undefined}
      data-dock-preview={docking.preview ?? undefined}
      data-dragging={docking.isDragging || undefined}
      data-pinned={(floating && docking.isPinned) || undefined}
      ref={(el) => {
        paneRef.current = el;
        if (floating) {
          setFloatingFocusedRef(el);
        }
      }}
      onScrollCapture={handleScrollVisibility}
      className={cn(
        styles.pane,
        floating && styles.agentChatPane,
        floating && isFloatingResizing && styles.paneResizing,
        documentMode && !floating && styles.paneDocumentMode,
        // The shell presentation (floating pill composer hovering over the
        // conversation) is shared by the floating popup and the document-mode
        // rail — the rail must look like the floating pane.
        (floating || documentMode) && styles.paneShell,
        floating && styles.paneComposerInline,
        floating && isGeneralDraft && styles.paneComposerGeneral,
        floating &&
          isFloatingSidebarOpen &&
          isFloatingPaneWide &&
          styles.agentChatSidebarOffset,
        className,
      )}
      style={
        floating
          ? ({
              width: floatingSize.width,
              height: floatingSize.height,
              // The stylesheet caps the popup height; the dragged height
              // must win over that cap.
              maxHeight: floatingSize.height,
              // Dragged sidebar width feeds the two-column grid.
              ...(floating && isFloatingSidebarOpen && isFloatingPaneWide
                ? { '--sidebar-w': `${floatingSidebarWidth}px` }
                : {}),
              // Top-corner drags lift the pane's top edge: the bottom
              // anchor shifts down by the same amount the pane grew.
              ...(floating && verticalShift
                ? { bottom: `calc(22px - ${verticalShift}px)` }
                : {}),
              ...(!isFloatingMaximized ||
              docking.isSideDocked ||
              docking.isDragging
                ? docking.style
                : {}),
            } as React.CSSProperties)
          : undefined
      }
      aria-label={formatMessage(patientAgentMessages.ariaLabelChat)}
    >
      {floating && docking.isSideDocked && !docking.isDragging && (
        <div
          role="separator"
          tabIndex={0}
          aria-orientation="vertical"
          aria-label={formatMessage(patientAgentMessages.resizeChat)}
          aria-valuemin={Math.min(360, window.innerWidth)}
          aria-valuemax={docking.maxSideWidth}
          aria-valuenow={Math.round(docking.sideWidth)}
          className={cn(
            styles.resizeHandle,
            styles.sideResizeHandle,
            docking.dock === 'left'
              ? styles.resizeHandleRight
              : styles.resizeHandleLeft,
          )}
          onPointerDown={docking.onSideResize}
          onKeyDown={(event) => {
            if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
              return;
            }
            event.preventDefault();
            docking.resizeSide(
              docking.sideWidth +
                (event.key === 'ArrowRight' ? 16 : -16) *
                  (docking.dock === 'left' ? 1 : -1),
            );
          }}
        />
      )}
      {floating && !docking.isSideDocked && !docking.isDragging ? (
        <>
          <div
            className={cn(styles.resizeHandle, styles.resizeHandleTop)}
            onPointerDown={startFloatingResize('top')}
          />
          <div
            className={cn(styles.resizeHandle, styles.resizeHandleLeft)}
            onPointerDown={startFloatingResize('left')}
          />
          <div
            className={cn(styles.resizeHandle, styles.resizeHandleRight)}
            onPointerDown={startFloatingResize('right')}
          />
          <div
            className={cn(styles.resizeHandle, styles.resizeHandleBottom)}
            onPointerDown={startFloatingResize('bottom')}
          />
          <div
            className={cn(
              styles.resizeHandle,
              styles.resizeHandleCorner,
              styles.resizeHandleCornerTL,
            )}
            onPointerDown={startFloatingResize('tl')}
          />
          <div
            className={cn(
              styles.resizeHandle,
              styles.resizeHandleCorner,
              styles.resizeHandleCornerTR,
            )}
            onPointerDown={startFloatingResize('tr')}
          />
          <div
            className={cn(
              styles.resizeHandle,
              styles.resizeHandleCorner,
              styles.resizeHandleCornerBL,
            )}
            onPointerDown={startFloatingResize('bl')}
          />
          <div
            className={cn(
              styles.resizeHandle,
              styles.resizeHandleCorner,
              styles.resizeHandleCornerBR,
            )}
            onPointerDown={startFloatingResize('br')}
          />
        </>
      ) : null}
      {floating && isFloatingSidebarOpen && !isFloatingPaneWide ? (
        <div
          key="floating-sidebar-backdrop"
          className={styles.agentChatSidebarBackdrop}
          onClick={() => setIsFloatingSidebarOpen(false)}
        />
      ) : null}
      {floating ? (
        <div
          key="floating-sidebar"
          className={cn(
            styles.agentChatSidebar,
            !isFloatingSidebarOpen && styles.agentChatSidebarHidden,
            !isFloatingPaneWide && styles.agentChatSidebarShutter,
          )}
          style={
            isFloatingPaneWide ? { width: floatingSidebarWidth } : undefined
          }
          aria-hidden={!isFloatingSidebarOpen}
        >
          <div className={styles.agentChatSidebarHeader}>
            <button
              type="button"
              className={styles.agentChatSidebarTrigger}
              aria-label={formatMessage(patientAgentMessages.closeSidebar)}
              title={formatMessage(patientAgentMessages.closeSidebar)}
              onClick={() => setIsFloatingSidebarOpen(false)}
            >
              <Icon name="list" size={20} />
            </button>
            {newChatTrigger}
          </div>
          {sidebarContent}
          {isFloatingPaneWide ? (
            <div
              className={cn(styles.resizeHandle, styles.sidebarResizeHandle)}
              onPointerDown={startSidebarResize}
            />
          ) : null}
        </div>
      ) : null}
      {!documentMode || floating ? (
        <div
          className={cn(styles.paneHeader, floating && styles.draggableHeader)}
          onPointerDown={floating ? docking.onPointerDown : undefined}
        >
          {floating ? (
            <div className={styles.agentChatPrimaryActions}>
              {!isFloatingSidebarOpen ? (
                <button
                  type="button"
                  className={styles.agentChatSidebarTrigger}
                  aria-label={formatMessage(patientAgentMessages.toggleSidebar)}
                  title={formatMessage(patientAgentMessages.toggleSidebar)}
                  onClick={() => setIsFloatingSidebarOpen(true)}
                >
                  <Icon name="list" size={20} />
                </button>
              ) : null}
              {!isFloatingSidebarOpen ? newChatTrigger : null}
            </div>
          ) : (
            <div className={styles.headerCopy}>
              <h3 className={cn('h6', styles.title)}>
                {formatMessage(patientAgentMessages.roleAgent)}
              </h3>
              {isThreadsOpen ? (
                <p className={cn('p3', styles.subtitle)}>
                  {formatMessage(patientAgentMessages.scopeThisPatient)}
                </p>
              ) : subtitle ? (
                <p className={cn('p3', styles.subtitle)}>{subtitle}</p>
              ) : null}
            </div>
          )}
          {floating && activeThreadTitle ? (
            <div
              key="paneHeaderTitle"
              className={styles.paneHeaderTitle}
              title={activeThreadTitle}
            >
              {activeThreadTitle}
            </div>
          ) : null}

          <div className={styles.headerActions}>
            {floating ? (
              <>
                {!docking.isDragging && (
                  <button
                    type="button"
                    className={styles.agentChatIconButton}
                    aria-pressed={docking.pinned}
                    aria-label={formatMessage(
                      docking.pinned
                        ? patientAgentMessages.unpinChat
                        : patientAgentMessages.pinChat,
                    )}
                    title={formatMessage(
                      !docking.canPin
                        ? patientAgentMessages.pinChatUnavailable
                        : docking.pinned
                          ? patientAgentMessages.unpinChat
                          : patientAgentMessages.pinChat,
                    )}
                    disabled={!docking.canPin && !docking.pinned}
                    onClick={docking.togglePin}
                  >
                    <Icon name="pin" size={22} />
                  </button>
                )}
                {!docking.isSideDocked && (
                  <button
                    type="button"
                    className={styles.agentChatIconButton}
                    aria-label={
                      isFloatingMaximized
                        ? formatMessage(
                            patientAgentMessages.restoreContextualChat,
                          )
                        : formatMessage(
                            patientAgentMessages.expandContextualChat,
                          )
                    }
                    title={
                      isFloatingMaximized
                        ? formatMessage(
                            patientAgentMessages.restoreContextualChat,
                          )
                        : formatMessage(
                            patientAgentMessages.expandContextualChat,
                          )
                    }
                    onClick={() => {
                      // Always maximize unless already fully expanded; the
                      // restore then brings back the previously dragged size.
                      if (isFloatingMaximized) {
                        setFloatingSize(expandRestoreRef.current || {});
                        setIsFloatingMaximized(false);
                        writeChatFloatingMaximized(false);
                      } else {
                        expandRestoreRef.current = floatingSize;
                        setFloatingSize(getMaximizedFloatingSize());
                        setIsFloatingMaximized(true);
                        writeChatFloatingMaximized(true);
                      }
                      setIsThreadsOpen(false);
                    }}
                  >
                    <Icon
                      name={isFloatingMaximized ? 'restore' : 'maximize'}
                      size={22}
                    />
                  </button>
                )}
                <button
                  type="button"
                  className={styles.agentChatIconButton}
                  aria-label={formatMessage(
                    patientAgentMessages.closeContextualChat,
                  )}
                  title={formatMessage(
                    patientAgentMessages.closeContextualChat,
                  )}
                  onClick={() => setIsFloatingConversationOpen(false)}
                >
                  <Icon name="close" size={22} />
                </button>
              </>
            ) : documentMode ? (
              <span className={styles.documentAwareness}>
                {formatMessage(patientAgentMessages.seesPatientStudies)}
              </span>
            ) : (
              <>
                <button
                  type="button"
                  className={styles.headerIconButton}
                  onClick={() => {
                    setIsThreadsOpen((currentValue) => !currentValue);
                    void loadThreads();
                  }}
                  aria-label={toggleLabel}
                  title={toggleLabel}
                >
                  {formatMessage(patientAgentMessages.sectionSessions)}
                </button>
                <button
                  type="button"
                  className={styles.headerIconButton}
                  onClick={resetConversation}
                  disabled={isRunning || isResolving}
                  aria-label={formatMessage(patientAgentMessages.buttonNew)}
                >
                  {formatMessage(patientAgentMessages.buttonNew)}
                </button>
              </>
            )}
          </div>
        </div>
      ) : null}

      <div className={styles.content}>
        {isResolving ? (
          <section className={styles.statusCard}>
            <div
              className={styles.loadingState}
              role="status"
              aria-live="polite"
            >
              <Spinner size={24} />
              <p className="p2">
                {formatMessage(patientAgentMessages.loadingPatientContext)}
              </p>
            </div>
          </section>
        ) : null}

        <ChatBody
          variant="shell"
          viewportRef={chatViewportRef}
          onScrollCapture={handleScrollVisibility}
          showWelcome={
            !threadLoading &&
            !displayedThreadItems.length &&
            !isResolving &&
            (!documentMode || floating)
          }
          welcomeProps={{
            patientName: patientDisplayName || undefined,
            entityName: threadEntityName,
            isGeneralDraft,
            writePresets,
            understandPresets,
            onPresetClick: insertPrompt,
          }}
          documentAssistantCopy={
            documentMode && !floating && displayedThreadItems.length === 0
              ? {
                  title: formatMessage(patientAgentMessages.startConversation),
                  body: documentID
                    ? formatMessage(
                        patientAgentMessages.noOriginatingConversation,
                      )
                    : formatMessage(patientAgentMessages.documentAssistantHint),
                }
              : null
          }
          isLoading={threadLoading && displayedThreadItems.length === 0}
          threadItems={displayedThreadItems.filter(
            (item) => item.type !== 'approval',
          )}
          streamdownKeySuffix={
            sourceAssetReportIDs === null ? 'loading' : 'resolved'
          }
          citationComponents={citationComponents}
          streamdownPlugins={streamdownPlugins}
          errorText={errorText}
          onOpenDocument={(documentID) => {
            Promise.resolve(onOpenDocument?.(documentID)).catch((reason) =>
              setErrorText(getErrorMessage(reason, intl)),
            );
          }}
          showDiscard={false}
          onDiscardDocument={(documentID) => void discardDocument(documentID)}
          onApprovalDecision={handleApprovalDecision}
          discardingDocumentID={discardingDocumentID}
          documentMode={documentMode}
          documentID={activeEditorKey}
          isRunning={isRunning}
          selectedDocumentText={selectedDocumentText}
          dismissedProposals={dismissedProposals}
          onReplaceDocumentSelection={onReplaceDocumentSelection}
          onInsertBelowDocumentSelection={onInsertBelowDocumentSelection}
          onDismissProposal={(itemID) =>
            setDismissedProposals((current) => new Set(current).add(itemID))
          }
          onAttachmentPreviewChange={onAttachmentPreviewChange}
          firstWorkEventID={firstWorkEventID}
          workContextLine={
            context.type === 'patient' && context.patientID
              ? formatMessage(patientAgentMessages.workContextPatient)
              : undefined
          }
        />
      </div>

      {pendingApproval ? (
        <div className={styles.composer}>
          <ApprovalCard
            item={pendingApproval}
            disabled={isRunning}
            takeover
            onDecision={handleApprovalDecision}
          />
        </div>
      ) : (
        <Composer
          onScrollToBottom={
            showScrollToBottom
              ? () => {
                  isAtBottomRef.current = true;
                  chatViewportRef.current?.scrollTo({
                    top: chatViewportRef.current.scrollHeight,
                    behavior: 'instant',
                  });
                  setShowScrollToBottom(false);
                }
              : undefined
          }
          floating={floating}
          isShell
          documentMode={documentMode}
          inputValue={inputValue}
          onInputChange={setInputValue}
          onKeyDown={handleComposerKeyDown}
          isRunning={isRunning}
          isResolving={isResolving || threadLoading || Boolean(pendingApproval)}
          hasThreadItems={displayedThreadItems.length > 0 || threadLoading}
          pendingAttachments={pendingAttachments}
          onRemoveAttachment={(id) => {
            const target = pendingAttachmentsRef.current.find(
              (item) => item.id === id,
            );
            if (target?.previewUrl) {
              URL.revokeObjectURL(target.previewUrl);
            }
            updatePendingAttachments((current) =>
              current.filter((item) => item.id !== id),
            );
          }}
          uploadingAttachment={uploadingAttachment}
          attachmentInputRef={attachmentInputRef}
          onAttachmentPick={handleAttachmentPick}
          isDraggingFiles={isDraggingFiles}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onPaste={handlePaste}
          patientDisplayName={patientDisplayName || undefined}
          isGeneralDraft={isGeneralDraft}
          scopeRemoveDisabled={isRunning}
          scopeSubject={scopeSubject}
          scopeEntityName={scopeEntityName}
          onScopeRestore={() => setIsGeneralDraft(false)}
          onScopeRemove={() => {
            setIsGeneralDraft(true);
          }}
          isThreadsOpen={isThreadsOpen}
          onAddContext={() => setIsThreadsOpen(true)}
          onSend={() => void sendPrompt(inputValue)}
        />
      )}
    </aside>
  );
  return (
    <>
      {floating ? createPortal(chatPane, document.body) : chatPane}
      {onboardingTour}
    </>
  );
};
